import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

interface SkinSelectorTooltipProps {
  isVisible: boolean;
  onClose: () => void;
}

export const SkinSelectorTooltip: React.FC<SkinSelectorTooltipProps> = ({ isVisible, onClose }) => {
  const { i18n, t } = useTranslation('common');
  const [animate, setAnimate] = useState(false);
  const [showAfterDelay, setShowAfterDelay] = useState(false);
  const [position, setPosition] = useState({ top: 70, left: 0, pointerLeft: 100 });

  // Délai de 2 secondes avant l'affichage
  useEffect(() => {
    if (isVisible) {
      const delayTimer = setTimeout(() => {
        setShowAfterDelay(true);
      }, 2000); // 2 secondes de délai

      return () => clearTimeout(delayTimer);
    } else {
      setShowAfterDelay(false);
    }
  }, [isVisible]);

  useEffect(() => {
    if (showAfterDelay) {
      // Calculer la position dynamiquement par rapport au SkinSwitcherDropdown
      const updatePosition = () => {
        const skinButton = document.getElementById('skin-switcher-button');
        if (skinButton) {
          const rect = skinButton.getBoundingClientRect();
          const tooltipWidth = 200; // Largeur approximative du tooltip
          
          // Position de la bulle : la pointe doit toucher la bordure du bouton, mais la bulle doit être en dessous de la navbar
          // La pointe est à -12px depuis le haut de la bulle, donc bubbleTop = rect.bottom + 10px pour que la bulle soit sous la navbar
          const bubbleTop = rect.bottom + 10; // 10px sous le bouton pour que la bulle soit clairement en dessous de la navbar
          const bubbleLeft = rect.left + (rect.width / 2) - (tooltipWidth / 2);
          
          // Position de la pointe : doit pointer vers le centre du bouton
          const pointerOffset = tooltipWidth / 2;
          
          setPosition({
            top: bubbleTop,
            left: Math.max(16, bubbleLeft), // Minimum 16px du bord
            pointerLeft: pointerOffset
          });
        }
      };

      // Calculer la position au montage et lors du redimensionnement
      updatePosition();
      window.addEventListener('resize', updatePosition);
      window.addEventListener('scroll', updatePosition);

      // Animation d'entrée
      setTimeout(() => setAnimate(true), 50);

      // Auto-fermeture après 5 secondes
      const timer = setTimeout(() => {
        setAnimate(false);
        setTimeout(() => {
          onClose();
          setShowAfterDelay(false);
        }, 300);
      }, 5000);

      return () => {
        clearTimeout(timer);
        window.removeEventListener('resize', updatePosition);
        window.removeEventListener('scroll', updatePosition);
      };
    } else {
      setAnimate(false);
    }
  }, [showAfterDelay, onClose, i18n.language]);

  if (!isVisible || !showAfterDelay) return null;

  const isRTL = i18n.dir(i18n.language) === 'rtl';

  return (
    <div
      className={`fixed z-[60] transition-all duration-500 ${
        animate ? 'opacity-100 scale-100 translate-y-0' : 'opacity-0 scale-95 translate-y-2'
      }`}
      style={{ 
        top: `${position.top}px`,
        ...(isRTL
          ? { right: `${position.left}px` }
          : { left: `${position.left}px` }
        ),
        transformOrigin: 'top center'
      }}
    >
      {/* Bulle de dialogue style BD */}
      <div className="relative">
        {/* Pointe de la bulle vers le HAUT (vers le Skin Selector) - touche la bordure */}
        <div 
          className="absolute"
          style={{
            top: '-12px', // Hauteur de la pointe qui touche exactement rect.bottom
            ...(isRTL
              ? { right: `${position.pointerLeft}px`, transform: 'translateX(50%)' }
              : { left: `${position.pointerLeft}px`, transform: 'translateX(-50%)' }
            )
          }}
        >
          <div className="relative">
            {/* Bordure du triangle pointant vers le haut */}
            <div className="w-0 h-0 border-l-[14px] border-l-transparent border-r-[14px] border-r-transparent border-b-[16px] border-b-purple-600 dark:border-b-purple-400"></div>
            {/* Remplissage du triangle */}
            <div className="absolute top-[3px] left-[2px] w-0 h-0 border-l-[12px] border-l-transparent border-r-[12px] border-r-transparent border-b-[14px] border-b-white dark:border-b-gray-800"></div>
          </div>
        </div>

        {/* Contenu de la bulle */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border-[3px] border-purple-600 dark:border-purple-400 px-5 py-3 min-w-[180px]">
          <p className="text-sm font-bold text-gray-900 dark:text-white text-center whitespace-nowrap">
            {t('skinTooltip.chooseUniverse')}
          </p>
        </div>
      </div>
    </div>
  );
};

export default SkinSelectorTooltip;

